import { useEffect, useMemo, useState } from 'react';

import { SelectOption } from '~/shared/utils/types';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { getDateFilterDates } from '~/shared/utils/getDateFilterDates';
import { UTILIZATION_TRAILING_LAG_MS, FUTURE_HEAD_PADDING_MS } from '~/shared/constants/polling';
import { splitAccountFilters } from '~/shared/utils/apiFilters';
import { appendAccountsScope } from '~/shared/utils/organizationUtils';
import { safeJsonParseArray } from '~/shared/utils/safeJson';

import { dateFilterItems } from './dateFilterMenuItems';
import {
  shouldShowFullGraph,
  getFilterSearchParams,
  getApiFilterFromSelectOption,
} from './helpers';
import { reportsFullGraphKey } from './serachParamKeys';
import { refreshStateMenuItems } from './refreshStateMenuItems';

type Params = {
  utilizationDateFilterSearchParam: string | null;
  showFullGraphSearchParam: string | null;
  apiFiltersSearchParam?: string | null;
  refreshStateSearchParams?: string | null;
  volumeId?: string;
  /** Enable trailing lag from "now" to reduce head jitter (defaults true) */
  useTrailingLag?: boolean;
  /** Override future head padding (ms) for end time (defaults FUTURE_HEAD_PADDING_MS) */
  futureHeadPaddingMs?: number;
};

export default function useUnifiedUtilizationQueryParams({
  utilizationDateFilterSearchParam,
  showFullGraphSearchParam,
  apiFiltersSearchParam,
  volumeId,
  refreshStateSearchParams,
  useTrailingLag = true,
  futureHeadPaddingMs = FUTURE_HEAD_PADDING_MS,
}: Params) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const { activeTenantId, accounts } = useAuthContext();

  // Derive refetch interval from selected refresh state; fallback to default unified interval.
  const refetchInterval = useMemo(() => {
    const match = refreshStateMenuItems.find((i) => i.title === refreshStateSearchParams);
    // If no selection, do NOT poll (match original reports behavior). Only poll when user selects.
    return match ? match.time : 0;
  }, [refreshStateSearchParams]);

  function computeNow() {
    return useTrailingLag ? new Date(Date.now() - UTILIZATION_TRAILING_LAG_MS) : new Date();
  }

  function onRefetchUtilizationQuery() {
    setCurrentDate(computeNow());
  }

  const showFullGraph = shouldShowFullGraph(!!showFullGraphSearchParam, activeTenantId);

  const activeDateFilterDuration = dateFilterItems.find(
    (f) => f.title === utilizationDateFilterSearchParam
  )?.duration;

  const [startDate, endDate] = useMemo(() => {
    const dateRange = getDateFilterDates(
      activeDateFilterDuration,
      utilizationDateFilterSearchParam,
      currentDate,
      { futureHeadPaddingMs }
    );
    return dateRange;
  }, [
    activeDateFilterDuration,
    currentDate,
    utilizationDateFilterSearchParam,
    futureHeadPaddingMs,
  ]);

  const parsedFilters = useMemo(
    () => safeJsonParseArray<SelectOption>(apiFiltersSearchParam),
    [apiFiltersSearchParam]
  );

  const { filtersWithoutAccount, accountIds } = useMemo(
    () => splitAccountFilters(parsedFilters),
    [parsedFilters]
  );

  const parsedFiltersSearchParams: string[] = useMemo(() => {
    const result = getApiFilterFromSelectOption(filtersWithoutAccount);
    return Array.isArray(result) ? result : [result];
  }, [filtersWithoutAccount]);

  const utilizationQueryUrl = useMemo(() => {
    const utilizationQueryParams = [
      ...parsedFiltersSearchParams.map((f) => ['filters', f]),
      ['start_date', startDate],
      ['end_date', endDate],
      ...(showFullGraph ? [[reportsFullGraphKey, 'true']] : []),
      ...(volumeId
        ? getFilterSearchParams([{ field: 'volumeId', value: volumeId!, op: 'equals' }])
        : []),
    ];

    const utilizationQs = new URLSearchParams(utilizationQueryParams);
    appendAccountsScope(utilizationQs, accounts, activeTenantId, accountIds);

    return `/api/v1/volumes/utilization-graph?${utilizationQs.toString()}`;
  }, [
    endDate,
    showFullGraph,
    startDate,
    volumeId,
    parsedFiltersSearchParams,
    accountIds,
    accounts,
    activeTenantId,
  ]);

  useEffect(() => {
    if (!refetchInterval) {
      return;
    }
    // Initialize immediately with (optional) trailing lag for stability
    setCurrentDate(computeNow());
    const id = setInterval(() => {
      setCurrentDate(computeNow());
    }, refetchInterval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchInterval, useTrailingLag]);

  return { utilizationQueryUrl, onRefetchUtilizationQuery, showFullGraph, startDate, endDate };
}
